'use client';

/**
 * FAQ accordion — categorised, flat, or CMS-backed. Ported from
 * ThemeMaker's {@code blocks/text/FaqAccordion/FaqAccordion.tsx}.
 *
 * <p>Rows use native {@code <details>} so they stay accessible and work
 * without JavaScript. CSS handles the chevron rotation + hover tinting.
 *
 * <h3>Data shape</h3>
 * <ul>
 *   <li>Pass {@code categories: [{label, items: [{question, answer}]}]}
 *       for a grouped FAQ.</li>
 *   <li>Or pass {@code items: [{question, answer}]} for a flat list.</li>
 *   <li>Categories takes precedence if both are supplied.</li>
 *   <li>With NEITHER authored, the block reads the tenant's CMS FAQ
 *       entries (rich-content {@code FAQ}) through {@link fetchFaqPage}.
 *       That's what the reserved {@code faq-landing} page renders —
 *       before this, an operator-authored FAQ collection had no portal
 *       surface at all and the landing showed an empty accordion.</li>
 * </ul>
 *
 * <p>CMS answers are loaded per row on expand ({@link fetchFaqAnswer}) —
 * the catalog indexes only the question + summary.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFaqAnswer, fetchFaqPage, type FaqEntry } from '@akademi/site-builder-runtime';
import { tx } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import { surfaceBackground, type SurfaceToken } from '../_shared/surface';

type Localized = string | Record<string, string>;

interface FaqItem {
  question?: Localized;
  answer?: Localized;
  /** CMS slug — present on entries loaded from the tenant's FAQ content. */
  slug?: string;
}

interface FaqCategory {
  label?: Localized;
  items?: FaqItem[];
}

export interface FaqAccordionProps {
  title?: Localized;
  subtitle?: Localized;
  categories?: FaqCategory[];
  items?: FaqItem[];
  /** Open the first item (per category, or globally for flat list). */
  openFirst?: boolean;
  /**
   * Max CMS entries pulled when the block falls back to tenant content.
   * Ignored when items/categories are authored on the block.
   */
  maxResults?: number;
  /**
   * Themed background the accordion sits on. Defaults to {@code 'none'}
   * (transparent) so existing sections are unchanged; the reserved
   * {@code faq-landing} page sets {@code 'card'} for a white
   * (theme-surface) panel that fills the containing section.
   */
  surface?: SurfaceToken;
}

export function FaqAccordion({
  title,
  subtitle,
  categories = [],
  items = [],
  openFirst = true,
  maxResults = 50,
  surface = 'none',
}: FaqAccordionProps) {
  const { t } = useTranslation('site-builder-blocks');
  const heading = title ?? t('faq.title');
  const useCategories = categories.length > 0;
  const authored = useCategories || items.length > 0;
  const [cmsItems, setCmsItems] = useState<FaqItem[]>([]);

  // No authored content → this is the CMS-backed FAQ hub.
  useEffect(() => {
    if (authored) return;
    let cancelled = false;
    void fetchFaqPage({ limit: maxResults }).then((page) => {
      if (!cancelled) setCmsItems(page.items.map(entryToItem));
    });
    return () => {
      cancelled = true;
    };
  }, [authored, maxResults]);

  const flatItems = useCategories ? [] : authored ? items : cmsItems;

  return (
    <SectionShell
      title={tx(heading)}
      subtitle={tx(subtitle)}
      background={surfaceBackground(surface)}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .lms-faq__details { border-bottom: 1px solid var(--color-border-subtle, rgba(0,0,0,0.08)); }
        .lms-faq__details > summary { list-style: none; cursor: pointer; padding: 20px 0; display: flex; align-items: flex-start; gap: 16px; user-select: none; }
        .lms-faq__details > summary::-webkit-details-marker { display: none; }
        .lms-faq__details > summary:hover .lms-faq__q { color: var(--color-brand-500, #722f37); }
        .lms-faq__chevron { flex-shrink: 0; width: 16px; height: 16px; transition: transform 220ms ease; color: var(--color-text-tertiary, #94867d); margin-top: 2px; }
        .lms-faq__details[open] .lms-faq__chevron { transform: rotate(45deg); color: var(--color-brand-500, #722f37); }
        .lms-faq__q { flex: 1; font-size: 17px; font-weight: 500; line-height: 1.4; color: var(--color-text-primary, #1a1614); transition: color 180ms ease; }
        .lms-faq__a { padding: 0 32px 24px 32px; font-size: 15px; line-height: 1.65; color: var(--color-text-secondary, #5d4f47); max-width: 720px; }
        .lms-faq__group { padding-bottom: 32px; }
        .lms-faq__group + .lms-faq__group { padding-top: 8px; }
        .lms-faq__cat { font-size: 11px; font-family: var(--font-family-mono, ui-monospace, monospace); text-transform: uppercase; letter-spacing: 0.12em; color: var(--color-text-tertiary, #94867d); padding-bottom: 8px; border-bottom: 1px solid var(--color-text-primary, #1a1614); margin-bottom: 4px; }
      `,
        }}
      />

      {useCategories
        ? categories.map((cat, ci) => (
            <div key={ci} className="lms-faq__group">
              {cat.label && <div className="lms-faq__cat">{tx(cat.label)}</div>}
              {(cat.items ?? []).map((item, i) => (
                <FaqRow key={i} item={item} openByDefault={openFirst && i === 0} />
              ))}
            </div>
          ))
        : flatItems.map((item, i) => (
            <FaqRow key={item.slug ?? i} item={item} openByDefault={openFirst && i === 0} />
          ))}
    </SectionShell>
  );
}

/** CMS entry → row item. The summary stands in until the body loads. */
function entryToItem(entry: FaqEntry): FaqItem {
  return { question: entry.question, answer: entry.answer, slug: entry.slug };
}

function FaqRow({ item, openByDefault }: { item: FaqItem; openByDefault: boolean }) {
  // CMS rows carry only a summary in the list payload; the full answer
  // markup is fetched once, on first expand.
  const [answerHtml, setAnswerHtml] = useState<string | null>(null);
  const slug = item.slug;

  const loadAnswer = useCallback(() => {
    if (!slug || answerHtml !== null) return;
    void fetchFaqAnswer(slug).then(setAnswerHtml);
  }, [slug, answerHtml]);

  useEffect(() => {
    if (openByDefault) loadAnswer();
  }, [openByDefault, loadAnswer]);

  return (
    <details className="lms-faq__details" open={openByDefault} onToggle={loadAnswer}>
      <summary>
        <span className="lms-faq__chevron" aria-hidden>
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </span>
        <span className="lms-faq__q">{tx(item.question)}</span>
      </summary>
      {answerHtml ? (
        <div className="lms-faq__a" dangerouslySetInnerHTML={{ __html: answerHtml }} />
      ) : (
        <div className="lms-faq__a">{tx(item.answer)}</div>
      )}
    </details>
  );
}
